import React, { useEffect } from "react";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAppContext } from "../../context/appContext";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import DataUtils from "../../utils/DataUtils";
import idApi from "../../service/IdApi";
import { useMutation } from "react-query";
import TokenService from "../../utils/TokenService";
import PageHeading from "../../components/PageHeading";
import { InputSwitch } from "primereact/inputswitch";
import { usStates } from "../../utils/AddressHelper";
import { throttle } from "lodash";
import { Mixpanel } from "../../utils/Mixpanel";

const NewUser = () => {
    const { countries, displayAlert } = useAppContext();
    const [submitted, setSubmitted] = useState(false);
    const [changesMade, setChangesMade] = useState(false);
    const [uriChangesMade, setUriChangesMade] = useState(false);
    const [companyName, setCompanyName] = useState("");
    const [uri, setUri] = useState("");
    const [invalidUri, setInvalidUri] = useState(false);
    const [invalidCompanyName, setInvalidCompanyName] = useState(false);
    const [invalidState, setInvalidState] = useState(false);
    const [invalidCountry, setInvalidCountry] = useState(false);
    const [invalidCity, setInvalidCity] = useState(false);
    const [zipCode, setZipCode] = useState("");
    const [invalidZipCode, setInvalidZipCode] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [city, setCity] = useState("");
    const [address, setAddress] = useState("");
    const [address2, setAddress2] = useState("");
    const [selectedUsState, setSelectedUsState] = useState({ name: '', code: '' });
    const [selectedCountry, setSelectedCountry] = useState({ name: "United States", code: "US" });
    const navigate = useNavigate();
    const [profileEnabled, setProfileEnabled] = useState(true);
    

    const createCompanyApi = useMutation(() => {
        var body = {};
        body.name = companyName.trim();
        if (profileEnabled) {
            body.uri = uri;
        }
        body.zipCode = zipCode.trim();
        if (address) {
            body.address = address.trim();
        }
        if (address2) {
            body.address2 = address2.trim();
        }
        body.city = city.trim();
        body.stateCode = selectedUsState.code;
        body.stateName = selectedUsState.name;
        body.countryCode = selectedCountry.code;
        body.countryName = selectedCountry.name;
        try {
            const data = idApi.post("auth/provider", body, {
                withCredentials: true,
            }).then((response) => response.data);
            return data;
        } catch (error) {
            throw error;
        }
    });

    const save = (e) => {
        e.preventDefault();
        submitAction();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        submitAction();
    }

    const submitAction = throttle(() => {
        setIsProcessing(true);
        setSubmitted(true);
        if (companyName.trim() === "") {
            displayAlert("error", "Error", "Company Name is required");
            setInvalidCompanyName(true);
            setIsProcessing(false);
            return;
        }
        if (companyName.trim().length < 3) {
            displayAlert("error", "Validation Error", "Company Name must be at least 3 characters");
            setInvalidCompanyName(true);
            setIsProcessing(false);
            return;
        }
        setInvalidCompanyName(false);
        if (profileEnabled) {
            if (uri.trim() === "") {
                displayAlert("error", "Error", "Wallist Profile URL is required");
                setInvalidUri(true);
                setIsProcessing(false);
                return;
            }
            if (uri.trim().length < 3) {
                displayAlert("error", "Validation Error", "The URL must be at least 3 characters");
                setInvalidUri(true);
                setIsProcessing(false);
                return;
            }
            var isValidUri = /^[A-Za-z0-9-]*$/.test(uri);
            if (!isValidUri) {
                displayAlert("error", "Validation Error", "The URL can only contain letters, numbers, and hyphens");
                setInvalidUri(true);
                setIsProcessing(false);
                return;
            }
            if (uri.trim().startsWith("-") || uri.trim().endsWith("-")) {
                displayAlert("error", "Validation Error", "The URL can not start or end with a hyphen");
                setInvalidUri(true);
                setIsProcessing(false);
                return;
            }
        }
        setInvalidUri(false);
        if (selectedCountry.code !== "US") {
            displayAlert("error", "Error", "The only country supported at this time is the United States");
            setInvalidCountry(true);
            setIsProcessing(false);
            return;
        }
        setInvalidCountry(false);
        // validate the city
        if (city.trim() === "") {
            displayAlert("error", "Error", "City is required");
            setInvalidCity(true);
            setIsProcessing(false);
            return;
        }
        setInvalidCity(false);
        // validate the state
        if (selectedUsState.code === "") {
            displayAlert("error", "Error", "State is required");
            setInvalidState(true);
            setIsProcessing(false);
            return;
        }
        setInvalidState(false);
        if (zipCode.trim() === "") {
            displayAlert("error", "Error", "Zip code is required");
            setInvalidZipCode(true);
            setIsProcessing(false);
            return;
        }
        if (!DataUtils.isUSZipValid(zipCode.trim(), false)) {
            displayAlert("error", "Error", "Zip code format is invalid");
            setInvalidZipCode(true);
            setIsProcessing(false);
            return;
        }
        setInvalidZipCode(false);

        // const { name, subdomain } = values;
        createCompanyApi.mutate({}, {
            onError: (error, variables, context) => {
                if (error && error.request.status === 409) {
                    displayAlert("error", "Unexpected Error", "This URL is taken. Please use a different value.");
                }
                else {
                    displayAlert("error", "Unexpected Error", "Please try again later");
                }
                setIsProcessing(false);
            },
            onSuccess: (data) => {
                TokenService.setAccessToken(data.userAccessToken);
                TokenService.setRefreshToken(data.userRefreshToken);
                displayAlert("success", "Success", "Provider created successfully");
                if (TokenService.getProviderSubdomain()) {
                    window.location.href = TokenService.getProviderUrl();
                }
            },
        });
    }, 500, { trailing: false });

    const handleUriChange = (e) => {
        setChangesMade(true);
        setUriChangesMade(true);
        setUri(e.target.value);
    };

    const handleZipChange = (e) => {
        setZipCode(e.target.value);
        setChangesMade(true);
    };

    const handleCompanyNameChange = (e) => {
        setChangesMade(true);
        setCompanyName(e.target.value);
        if (!uriChangesMade) {
            // we should suggest a URI
            // replace spaces with hyphens
            var withoutSpaces = e.target.value.replace(/\s/g, '-');
            var withoutBadChars = withoutSpaces.replace(/[^A-Za-z0-9-]/g, '');
            var noStartEndHyphens = withoutBadChars.replace(/^-+|-+$/g, '');
            var noDoubleHyphens = noStartEndHyphens.replace(/-+/g, '-');
            setUri(noDoubleHyphens);
        }
    };

    const handleUsStateChange = (e) => {
        setSelectedUsState(e.value);
        setChangesMade(true);
    };

    const handleCityChange = (e) => {
        setCity(e.target.value);
        setChangesMade(true);
    };

    const handleAddressChange = (e) => {
        setAddress(e.target.value);
        setChangesMade(true);
    };

    const handleAddress2Change = (e) => {
        setAddress2(e.target.value);
        setChangesMade(true);
    };

    const cancel = (e) => {
        navigate("/account/select-provider");
    };

    const handleCountryChange = (e) => {
        setSelectedCountry(e.value);
        setChangesMade(true);
    };

    const pageHeader = (
        <PageHeading pageTitle="Company Setup">
            <div className="block">
                <Button label="Submit" icon="pi pi-check" className="mr-2" disabled={!changesMade} onClick={save} loading={isProcessing} />
                <Button label="Cancel" icon="pi pi-times" className="p-button-danger" onClick={cancel} />
            </div>
        </PageHeading>
    );

    useEffect(() => {
        Mixpanel.track("UI ID - New Provider page view");
    }, []);

    return (
        <div >
            {pageHeader}
            <div className="layout-content">
                <div className="pages-panel card flex flex-column">
                    <form className="form" onSubmit={onSubmit}>
                        <div className="p-fluid formgrid grid text-left">
                            <div className="field col-12">
                                <div className='text-xl font-semibold mb-1'>
                                    General Information
                                </div>
                                <div>
                                    To get started, enter your company name and location. This information is used to establish your initial profile and field service management configuration and can be changed at any time.
                                </div>
                            </div>

                            <div className="field col-12">
                                <label htmlFor="name" className={classNames("required", { "text-pink-400": submitted && invalidCompanyName })}>
                                    Company Name
                                </label>
                                <InputText
                                    id="companyName"
                                    name="companyName"
                                    value={companyName}
                                    onChange={handleCompanyNameChange}
                                    className={classNames({ "p-invalid": submitted && invalidCompanyName })}
                                    maxLength={46}
                                    required
                                    autoFocus
                                />
                            </div>
                            {/* {profileEnabled && (
                                <div className='ml-3 pl-1 text-700'>
                                    You will be able to update your profile and upload photos at:<br />
                                    <span className="text-primary">{"wallist.com/company/" + uri}</span>
                                </div>
                            )} */}
                            <div className="field col-12">
                                <label htmlFor="country" className={classNames("required", { "text-pink-400": submitted && invalidCountry })}>Country</label>
                                <Dropdown
                                    id="country"
                                    name="country"
                                    value={selectedCountry}
                                    defaultValue="US"
                                    onChange={handleCountryChange}
                                    options={countries}
                                    optionLabel="name"
                                    className={classNames("", { "p-invalid": submitted && invalidCountry })}
                                    placeholder="Select One"
                                ></Dropdown>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="address">Address</label><span className="text-sm text-700">&nbsp;(optional if you don't have a physical location)</span>
                                <InputText
                                    id="address"
                                    name="address"
                                    value={address || ""}
                                    onChange={handleAddressChange}
                                    maxLength={46}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="address2">Address 2</label>
                                <InputText
                                    id="address2"
                                    name="address2"
                                    value={address2 || ""}
                                    onChange={handleAddress2Change}
                                    maxLength={46}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="city" className={classNames("required", { "text-pink-400": submitted && invalidCity })}>City</label>
                                <InputText
                                    id="city"
                                    name="city"
                                    value={city}
                                    type="text"
                                    onChange={handleCityChange}
                                    className={classNames("", { "p-invalid": submitted && invalidCity })}
                                    maxLength={50}
                                />
                            </div>
                            <div className="field col-12 md:col-3">
                                <label htmlFor="state" className={classNames("required", { "text-pink-400": submitted && invalidState })}>State</label>
                                <Dropdown
                                    id="state"
                                    name="state"
                                    value={selectedUsState}
                                    onChange={handleUsStateChange}
                                    options={usStates}
                                    optionLabel="name"
                                    placeholder="Select One"
                                    className={classNames("", { "p-invalid": submitted && invalidState })}
                                ></Dropdown>
                            </div>
                            <div className="field col-12 md:col-3">
                                <label htmlFor="zipCode" className={classNames("required", { "text-pink-400": submitted && invalidZipCode })}>
                                    Zip Code
                                </label>
                                <InputText
                                    id="zipCode"
                                    name="zipCode"
                                    value={zipCode}
                                    onChange={handleZipChange}
                                    className={classNames("", { "p-invalid": submitted && invalidZipCode })}
                                    maxLength="10"
                                />
                            </div>
                            <div className="field col-12">
                                <div className='text-xl font-semibold mb-1'>
                                    Public Profile
                                </div>
                                <div>
                                    With Wallist, you can create a free public profile for your business. This helps establish your online presence so customers can find you. If you already have a company website, linking to it from your Wallist profile helps by creating reputable backlinks for Search Engine Optimization (SEO).  If your business does not have a website and you would like your own custom domain, please email <a href="mailto:support@wallist.com">support@wallist.com</a> and we will help get you set up!
                                </div>
                            </div>
                            {/* <div className="field col-12">
                                <div className="flex">
                                    <label htmlFor="uriEnabled" className={classNames("mr-3", { "text-pink-400": submitted && invalidUri })}>
                                        Create Profile
                                    </label>
                                    <InputSwitch checked={profileEnabled} onChange={(e) => setProfileEnabled(e.value)} />
                                </div>
                            </div> */}
                            <div className="field col-12">
                                <div className="flex">
                                    <label htmlFor="uri" className={classNames("mt-1 mr-3", { "text-pink-400": submitted && invalidUri, "required": profileEnabled })}>
                                        Profile URL
                                    </label>
                                    <InputSwitch checked={profileEnabled} onChange={(e) => setProfileEnabled(e.value)} />
                                </div>
                                {profileEnabled && (
                                    <div className="p-inputgroup mt-2">
                                        <span className="p-inputgroup-addon pr-0 mr-0">
                                            wallist.com/company/
                                        </span>
                                        <InputText
                                            id="uri"
                                            name="uri"
                                            value={uri}
                                            onChange={handleUriChange}
                                            required
                                            className={classNames("ml-0 pl-0", { "p-invalid": submitted && invalidUri })}
                                            maxLength={75}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <input type="submit" style={{ display: "none" }} />
                    </form>
                </div>
            </div>
        </div>
    );
};

export default NewUser;
